import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type MouseEvent as RMouseEvent } from 'react';
import { Icon } from './Icon';
import { layoutFor, byId } from '../logik/model.js';
import { renderDiagram } from '../logik/render/svg.js';
import { sync, darf, aufServer } from '../sync';
import { useAlleKommentare, kommentareJeSchritt } from '../kommentare';
import { useOffeneAufgaben, offeneJeSchritt as aufgabenJeSchritt } from '../offeneAufgaben';
import { DIM, locate } from '../logik/layout/index.js';
import { moveNode, addEdge, addNode, placeNode, setNodeField, reverseEdge, deleteEdge } from '../logik/actions.js';
import { STATUS, NODE_TYPES } from '../logik/model.js';
import { menueOeffnen, perTastatur, adresse, kopieren, feldFokussieren, wartenAuf } from './Kontextmenue';
import { schrittMenue, bereinigen, type Zeile } from '../kontextmenue';
import { schrittLoeschen } from './Panel';
import { toast } from './toast';
import { Legend } from './Legend';
import type { Auswahl } from './Panel';

const NS = 'http://www.w3.org/2000/svg';

// Symbolleiste im Bearbeitungsmodus: Reihenfolge und kleine Symbole der Schritttypen (28 × 20)
const PALETTE = ['aktivitaet', 'automatik', 'beleg', 'gateway', 'entscheidung', 'subprozess', 'start', 'ende'];
const typName = (typ: string) => NODE_TYPES.find((t) => t.id === typ)?.name || typ;
// Kurzbeschreibung je Schritttyp für den Tooltip der Symbolleiste
const TYP_INFO: Record<string, string> = {
  aktivitaet: 'Arbeitsschritt, den eine Person oder Abteilung von Hand erledigt.',
  automatik: 'Schritt, den ein System selbst erledigt, z. B. Belegworkflow oder Datenübergabe per Schnittstelle.',
  beleg: 'Ein Dokument entsteht oder wird weitergegeben, z. B. Angebot, Auftrag oder Lieferschein.',
  gateway: 'Der Ablauf teilt sich je nach Bedingung – die abgehenden Pfeile mit „ja“/„nein“ beschriften.',
  entscheidung: 'Noch ungeklärte Frage zum Ablauf; legt eine Entscheidung mit Varianten zum Ausfüllen an.',
  subprozess: 'Fasst einen eigenen, detaillierten Ablauf zusammen, der sich per Klick öffnen lässt.',
  start: 'Auslöser, mit dem der Prozess beginnt.',
  ende: 'Ergebnis, mit dem der Prozess abschließt.',
};
function Symbol({ typ }: { typ: string }) {
  const c = 'currentColor';
  const box = <rect x="2" y="3" width="24" height="14" rx="2.5" fill="none" stroke={c} strokeWidth="1.4" />;
  const inhalt: Record<string, any> = {
    aktivitaet: <>{box}<rect x="2" y="3" width="3.5" height="14" rx="1.2" fill="var(--pa-accent)" /></>,
    automatik: <><rect x="2" y="3" width="24" height="14" rx="2.5" fill="var(--pa-accent)" fillOpacity=".15" stroke={c} strokeWidth="1.4" /><circle cx="20" cy="12" r="2.6" fill="none" stroke={c} strokeWidth="1.3" /></>,
    beleg: <>{box}<path d="M17 7.5h4.5l1.5 1.5v5h-6z" fill="none" stroke={c} strokeWidth="1.1" /></>,
    gateway: <path d="M14 1.5 24 10 14 18.5 4 10z" fill="var(--pa-accent)" fillOpacity=".2" stroke={c} strokeWidth="1.4" />,
    entscheidung: <><rect x="2" y="3" width="24" height="14" rx="2.5" fill="none" stroke="#E67E22" strokeWidth="1.6" strokeDasharray="3 2" /><text x="14" y="14" fontSize="10" fontWeight="700" textAnchor="middle" fill="#E67E22">?</text></>,
    subprozess: <>{box}<rect x="4.5" y="5.5" width="19" height="9" rx="1.5" fill="none" stroke={c} strokeWidth="1" /><path d="M14 7.5v5M11.5 10h5" stroke={c} strokeWidth="1.2" /></>,
    start: <rect x="2" y="4" width="24" height="12" rx="6" fill="none" stroke="#2E8B57" strokeWidth="2" />,
    ende: <rect x="2" y="4" width="24" height="12" rx="6" fill="none" stroke={c} strokeWidth="2.8" />,
  };
  return <svg width="28" height="20" viewBox="0 0 28 20" aria-hidden="true">{inhalt[typ]}</svg>;
}

interface DiagramViewProps {
  data: any;
  pid: string;
  sel: Auswahl;
  edit: boolean;
  zielbild: boolean;
  onSelect: (s: Auswahl) => void;
  onDrill: (pid: string) => void;
  /** Bearbeitungsmodus einschalten (Kontextmenü „Bearbeiten“) */
  onBearbeiten?: () => void;
  /** Früherer Stand: keine Kommentar-/Aufgabenzähler, im Kontextmenü nur die Ansicht */
  nurAnsicht?: boolean;
  /** Unterschiede hervorheben: Schritt- bzw. Verbindungs-ID → neu, geaendert oder geloescht */
  markierung?: Record<string, string>;
}

export function DiagramView({ data, pid, sel, edit, zielbild, onSelect, onDrill, onBearbeiten, nurAnsicht, markierung }: DiagramViewProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const overlayRef = useRef<SVGGElement>(null);
  const view = useRef({ x: 0, y: 0, k: 1 });
  const drag = useRef<any>(null);
  // Touch: aktive Finger (Pointer-ID → Position) und laufende Zwei-Finger-Geste
  const finger = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<null | { d: number; mx: number; my: number; v: { x: number; y: number; k: number } }>(null);
  const [legend, setLegendState] = useState(() => {
    try { return localStorage.getItem('prozessatlas:legende') === '1'; } catch { return false; }
  });
  const setLegend = (v: boolean) => {
    setLegendState(v);
    try { localStorage.setItem('prozessatlas:legende', v ? '1' : '0'); } catch { /* egal */ }
  };

  const L: any = useMemo(() => layoutFor(data, pid, { zielbild }), [data, pid, zielbild]);
  const alleKommentare = useAlleKommentare(sync.projektId);
  const kommentare = useMemo(() => kommentareJeSchritt(alleKommentare), [alleKommentare]);
  const alleAufgaben = useOffeneAufgaben(sync.projektId);
  const aufgaben = useMemo(() => aufgabenJeSchritt(alleAufgaben), [alleAufgaben]);
  const inner = useMemo(
    () => renderDiagram(data, L.layout, L.nodes, nurAnsicht ? { selected: sel, zielbild, edit } : { selected: sel, zielbild, edit, kommentare, aufgaben }),
    [data, L, sel, zielbild, edit, kommentare, aufgaben, nurAnsicht],
  );
  // Unterschiede zu einem anderen Stand einfärben (nach jedem Neuzeichnen)
  useEffect(() => {
    if (!markierung || !gRef.current) return;
    for (const el of gRef.current.querySelectorAll('[data-node], [data-edge]')) {
      const art = markierung[el.getAttribute('data-node') ?? el.getAttribute('data-edge') ?? ''];
      el.classList.remove('mark-neu', 'mark-geaendert', 'mark-geloescht');
      if (art) el.classList.add('mark-' + art);
    }
  }, [inner, markierung]);

  const apply = () => {
    const v = view.current;
    if (gRef.current) gRef.current.setAttribute('transform', `translate(${v.x},${v.y}) scale(${v.k})`);
  };
  const fit = (all = false) => {
    const el = wrapRef.current;
    if (!el) return;
    const w = el.clientWidth, h = el.clientHeight;
    const pad = 28;
    const k = Math.min((w - pad * 2) / L.layout.width, (h - pad * 2 - 50) / L.layout.height, 1.15);
    const kk = Math.max(0.15, k);
    view.current = { k: kk, x: Math.max(pad, (w - L.layout.width * kk) / 2), y: Math.max(pad + 46, (h - L.layout.height * kk) / 2) };
    // Sehr breite Diagramme: lesbar halten und links beginnen (Rest per Ziehen/Scrollen)
    if (k < 0.75 && !all) {
      // Höhe ausnutzen: so groß wie möglich (75–100 %), links beginnen, Rest per Ziehen/Scrollen
      const r = Math.min(1, Math.max(0.75, (h - pad * 2 - 50) / L.layout.height));
      view.current = { k: r, x: pad, y: Math.max(pad + 46, (h - L.layout.height * r) / 2 + 20) };
    }
    apply();
  };
  const zoom = (f: number, cx?: number, cy?: number) => {
    const el = wrapRef.current!;
    const v = view.current;
    const px = cx ?? el.clientWidth / 2, py = cy ?? el.clientHeight / 2;
    const k = Math.min(3, Math.max(0.12, v.k * f));
    view.current = { k, x: px - ((px - v.x) * k) / v.k, y: py - ((py - v.y) * k) / v.k };
    apply();
  };

  useEffect(() => fit(false), [pid, zielbild]);
  useEffect(apply);
  // Ausgewählten Knoten sichtbar machen (z. B. nach Suche/Deep-Link)
  useEffect(() => {
    if (!sel || sel.type !== 'node') return;
    const p = L.layout.nodes[sel.id];
    const el = wrapRef.current;
    if (!p || !el) return;
    const v = view.current;
    const sx = p.x * v.k + v.x, sy = p.y * v.k + v.y;
    if (sx < 0 || sy < 40 || sx + p.w * v.k > el.clientWidth || sy + p.h * v.k > el.clientHeight) {
      view.current = { ...v, x: el.clientWidth / 2 - (p.x + p.w / 2) * v.k, y: el.clientHeight / 2 - (p.y + p.h / 2) * v.k };
      apply();
    }
  }, [sel && sel.id, pid]);

  const toDiagram = (e: { clientX: number; clientY: number }) => {
    const r = wrapRef.current!.getBoundingClientRect();
    const v = view.current;
    return { x: (e.clientX - r.left - v.x) / v.k, y: (e.clientY - r.top - v.y) / v.k };
  };

  const clearOverlay = () => {
    if (overlayRef.current) overlayRef.current.innerHTML = '';
  };
  const el = (tag: string, attrs: Record<string, string | number>) => {
    const n = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
    overlayRef.current!.appendChild(n);
    return n;
  };

  // Gestrichelter Platzhalter an der Stelle, an der ein Schritt landet
  const zeigeSlot = (slot: Element, tgt: { lane: string; col: number; row: number }) => {
    const lane = L.layout.lanes.find((l: any) => l.id === tgt.lane);
    const cols = L.layout.cols;
    const x = !cols.length ? DIM.laneHeader + DIM.edgeChannel : tgt.col < cols.length ? cols[tgt.col].x : cols[cols.length - 1].x + DIM.W + DIM.channelBase;
    const y = lane.y + lane.corridor + Math.min(tgt.row, lane.rows) * (DIM.H + DIM.rowGap);
    slot.setAttribute('x', String(x));
    slot.setAttribute('y', String(Math.min(y, lane.y + lane.h - DIM.H / 2)));
  };
  // Schritt oder Verbindung unter dem Zeiger (Overlay-Elemente lassen den Zeiger durch)
  const zielUnter = (cx: number, cy: number): null | { node: string } | { edge: string } => {
    const under = document.elementFromPoint(cx, cy);
    if (!under || !wrapRef.current!.contains(under)) return null;
    const n = under.closest('[data-node]')?.getAttribute('data-node');
    if (n) return { node: n };
    const ed = under.closest('[data-edge]')?.getAttribute('data-edge');
    return ed ? { edge: ed } : null;
  };
  // Hervorhebung des Ziels: Rahmen um den Schritt bzw. Spur über der Verbindung
  const markiere = (mark: Element, ziel: ReturnType<typeof zielUnter>) => {
    let d = '';
    if (ziel && 'node' in ziel) {
      const p = L.layout.nodes[ziel.node];
      if (p) d = `M${p.x - 5},${p.y - 5}h${p.w + 10}v${p.h + 10}h${-p.w - 10}z`;
    } else if (ziel && 'edge' in ziel) {
      const e = L.layout.edges.find((x: any) => x.id === ziel.edge);
      if (e) d = 'M' + e.points.map((pt: number[]) => pt.join(',')).join(' L');
    }
    mark.setAttribute('d', d);
  };

  // ---------- Symbolleiste: Schritttyp ins Diagramm ziehen ----------
  const palette = useRef<any>(null);
  const paletteGezogen = useRef(false);
  const paletteDown = (e: RPointerEvent<HTMLButtonElement>, typ: string) => {
    if (e.button !== 0) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* egal */ }
    palette.current = { typ, cx: e.clientX, cy: e.clientY, aktiv: false };
  };
  const paletteMove = (e: RPointerEvent<HTMLButtonElement>) => {
    const d = palette.current;
    if (!d) return;
    if (!d.aktiv) {
      if (Math.hypot(e.clientX - d.cx, e.clientY - d.cy) < 5) return;
      d.aktiv = true;
      clearOverlay();
      d.mark = el('path', { d: '', fill: 'none', stroke: 'var(--pa-accent)', 'stroke-width': 6, 'stroke-opacity': 0.45, 'stroke-linejoin': 'round', class: 'ghost' });
      d.slot = el('rect', { width: DIM.W, height: DIM.H, rx: 8, fill: 'none', stroke: 'var(--pa-accent)', 'stroke-width': 2, 'stroke-dasharray': '6 4', class: 'ghost' });
      d.ghost = el('rect', { width: DIM.W, height: DIM.H, rx: d.typ === 'start' || d.typ === 'ende' ? DIM.H / 2 : 8, fill: 'var(--pa-accent)', 'fill-opacity': 0.12, stroke: 'var(--pa-accent)', 'stroke-width': 1.5, class: 'ghost' });
      d.text = el('text', { 'text-anchor': 'middle', 'font-size': 14, fill: 'var(--pa-accent)', class: 'ghost' });
      d.text.textContent = typName(d.typ);
    }
    const r = wrapRef.current!.getBoundingClientRect();
    d.drin = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    overlayRef.current!.style.display = d.drin ? '' : 'none';
    if (!d.drin) return;
    const pt = toDiagram(e);
    d.ghost.setAttribute('x', pt.x - DIM.W / 2);
    d.ghost.setAttribute('y', pt.y - DIM.H / 2);
    d.text.setAttribute('x', pt.x);
    d.text.setAttribute('y', pt.y + 5);
    d.ziel = zielUnter(e.clientX, e.clientY);
    d.target = locate(L.layout, pt.x, pt.y);
    markiere(d.mark, d.ziel);
    d.slot.style.display = d.ziel ? 'none' : '';
    if (!d.ziel) zeigeSlot(d.slot, d.target);
  };
  const paletteUp = () => {
    const d = palette.current;
    palette.current = null;
    if (!d || !d.aktiv) return;
    paletteGezogen.current = true;
    clearOverlay();
    overlayRef.current!.style.display = '';
    if (!d.drin) return;
    const id = d.ziel && 'node' in d.ziel ? placeNode(pid, { typ: d.typ, after: d.ziel.node, layout: L.layout })
      : d.ziel && 'edge' in d.ziel ? placeNode(pid, { typ: d.typ, into: d.ziel.edge, target: d.target })
      : placeNode(pid, { typ: d.typ, target: d.target, layout: L.layout });
    onSelect({ type: 'node', id });
  };
  const paletteCancel = () => {
    palette.current = null;
    clearOverlay();
    if (overlayRef.current) overlayRef.current.style.display = '';
  };
  // Klick oder Tastatur ohne Ziehen: wie bisher als Nachfolger des ausgewählten Schritts
  const paletteClick = (typ: string) => {
    if (paletteGezogen.current) { paletteGezogen.current = false; return; }
    add(typ);
  };

  // Mittelpunkt und Abstand der ersten beiden Finger, relativ zum Diagramm
  const zweiFinger = () => {
    const [a, b] = [...finger.current.values()];
    const r = wrapRef.current!.getBoundingClientRect();
    return { d: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mx: (a.x + b.x) / 2 - r.left, my: (a.y + b.y) / 2 - r.top };
  };

  const onPointerDown = (e: RPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (e.pointerType === 'touch') {
      finger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (finger.current.size >= 2) {
        // Zweiter Finger: laufendes Verschieben/Ziehen abbrechen, Zoomen beginnt
        if (finger.current.size === 2) pinch.current = { ...zweiFinger(), v: { ...view.current } };
        drag.current = null;
        clearOverlay();
        wrapRef.current!.classList.remove('panning');
        return;
      }
    }
    const t = e.target as Element;
    const port = t.closest('[data-port]');
    const nodeEl = t.closest('[data-node]');
    const edgeEl = t.closest('[data-edge]');
    const drillEl = t.closest('[data-drill]');
    try { wrapRef.current!.setPointerCapture(e.pointerId); } catch { /* z. B. nachgestellte Ereignisse */ }
    const start = { cx: e.clientX, cy: e.clientY, vx: view.current.x, vy: view.current.y, touch: e.pointerType === 'touch', t0: Date.now() };
    if (port && edit) {
      const from = port.getAttribute('data-port')!;
      const p = L.layout.nodes[from];
      clearOverlay();
      const mark = el('path', { d: '', fill: 'none', stroke: 'var(--pa-accent)', 'stroke-width': 6, 'stroke-opacity': 0.45, class: 'ghost' });
      const slot = el('rect', { width: DIM.W, height: DIM.H, rx: 8, fill: 'var(--pa-accent)', 'fill-opacity': 0.06, stroke: 'var(--pa-accent)', 'stroke-width': 2, 'stroke-dasharray': '6 4', class: 'ghost', style: 'display:none' });
      const line = el('path', { d: '', stroke: 'var(--pa-accent)', 'stroke-width': 2, 'stroke-dasharray': '5 4', fill: 'none', class: 'ghost' });
      drag.current = { mode: 'connect', from, line, mark, slot, sx: p.x + p.w, sy: p.y + p.h / 2, ...start };
    } else if (nodeEl) {
      drag.current = { mode: 'press', id: nodeEl.getAttribute('data-node'), drill: drillEl?.getAttribute('data-drill') || null, ...start };
    } else if (edgeEl) {
      drag.current = { mode: 'edge', id: edgeEl.getAttribute('data-edge'), ...start };
    } else {
      drag.current = { mode: 'pan', ...start };
      wrapRef.current!.classList.add('panning');
    }
  };

  const onPointerMove = (e: RPointerEvent<HTMLDivElement>) => {
    if (finger.current.has(e.pointerId)) finger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = pinch.current;
    if (p && finger.current.size >= 2) {
      // Zwei-Finger-Zoom um den Mittelpunkt, zugleich Verschieben mit dem Mittelpunkt
      const { d, mx, my } = zweiFinger();
      const k = Math.min(3, Math.max(0.12, (p.v.k * d) / p.d));
      view.current = { k, x: mx - ((p.mx - p.v.x) * k) / p.v.k, y: my - ((p.my - p.v.y) * k) / p.v.k };
      apply();
      return;
    }
    const d = drag.current;
    if (!d) return;
    // Finger zittern mehr als die Maus: größere Schwelle, ab der aus Tippen Verschieben wird
    const moved = Math.hypot(e.clientX - d.cx, e.clientY - d.cy) > (d.touch ? 10 : 4);
    if (d.mode === 'pan') {
      view.current = { ...view.current, x: d.vx + e.clientX - d.cx, y: d.vy + e.clientY - d.cy };
      apply();
    } else if (d.mode === 'press' && moved && edit && (!d.touch || Date.now() - d.t0 > 400)) {
      // Touch: Schritt erst nach kurzem Halten ziehen, sonst verschiebt der Finger die Ansicht
      d.mode = 'drag';
      clearOverlay();
      d.ghost = el('rect', { width: DIM.W, height: DIM.H, rx: 8, fill: 'var(--pa-accent)', 'fill-opacity': 0.12, stroke: 'var(--pa-accent)', 'stroke-width': 1.5, class: 'ghost' });
      d.slot = el('rect', { width: DIM.W, height: DIM.H, rx: 8, fill: 'none', stroke: 'var(--pa-accent)', 'stroke-width': 2, 'stroke-dasharray': '6 4', class: 'ghost' });
    } else if (d.mode === 'press' && moved) {
      d.mode = 'pan';
      wrapRef.current!.classList.add('panning');
    }
    if (d.mode === 'drag') {
      const pt = toDiagram(e);
      d.ghost.setAttribute('x', pt.x - DIM.W / 2);
      d.ghost.setAttribute('y', pt.y - DIM.H / 2);
      d.target = locate(L.layout, pt.x, pt.y);
      zeigeSlot(d.slot, d.target);
    }
    if (d.mode === 'connect') {
      const pt = toDiagram(e);
      d.line.setAttribute('d', `M${d.sx},${d.sy} L${pt.x},${pt.y}`);
      // Über einem Schritt: Rahmen; über freier Fläche: Platzhalter für einen neuen Nachfolger
      const ziel = zielUnter(e.clientX, e.clientY);
      d.ziel = ziel && 'node' in ziel && ziel.node !== d.from ? ziel : null;
      markiere(d.mark, d.ziel);
      d.leer = !ziel && moved;
      d.slot.style.display = d.leer ? '' : 'none';
      if (d.leer) { d.target = locate(L.layout, pt.x, pt.y); zeigeSlot(d.slot, d.target); }
    }
  };

  const onPointerUp = (e: RPointerEvent<HTMLDivElement>) => {
    finger.current.delete(e.pointerId);
    if (pinch.current) {
      // Nach dem Zoomen nichts auswählen; erst wenn alle Finger oben sind, ist die Geste vorbei
      if (finger.current.size < 2) pinch.current = null;
      drag.current = null;
      return;
    }
    const d = drag.current;
    drag.current = null;
    wrapRef.current!.classList.remove('panning');
    if (!d) return;
    const moved = Math.hypot(e.clientX - d.cx, e.clientY - d.cy) > (d.touch ? 10 : 4);
    if (d.mode === 'drag') {
      clearOverlay();
      if (d.target) moveNode(d.id, d.target, L.layout);
      return;
    }
    if (d.mode === 'connect') {
      clearOverlay();
      const ziel = zielUnter(e.clientX, e.clientY);
      if (ziel && 'node' in ziel) {
        const id = addEdge(pid, d.from, ziel.node);
        if (id) onSelect({ type: 'edge', id });
      } else if (!ziel && moved) {
        // Auf freie Fläche gezogen: dort einen neuen, verbundenen Schritt anlegen
        const target = locate(L.layout, toDiagram(e).x, toDiagram(e).y);
        onSelect({ type: 'node', id: placeNode(pid, { after: d.from, target, layout: L.layout }) });
      }
      return;
    }
    if (d.mode === 'press') {
      const n = byId(data.nodes, d.id);
      if (d.drill) return onDrill(d.drill);
      if (!edit && n && n.typ === 'subprozess' && n.subprocessId) return onDrill(n.subprocessId);
      return onSelect({ type: 'node', id: d.id });
    }
    if (d.mode === 'edge') return onSelect({ type: 'edge', id: d.id });
    if (d.mode === 'pan' && !moved) onSelect(null);
  };

  const onPointerCancel = (e: RPointerEvent<HTMLDivElement>) => {
    finger.current.delete(e.pointerId);
    if (finger.current.size < 2) pinch.current = null;
    if (drag.current) clearOverlay();
    drag.current = null;
    wrapRef.current!.classList.remove('panning');
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const r = wrapRef.current!.getBoundingClientRect();
    if (e.ctrlKey || e.metaKey) zoom(Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
    else {
      view.current = { ...view.current, x: view.current.x - e.deltaX, y: view.current.y - e.deltaY };
      apply();
    }
  };

  const onDblClick = (e: RMouseEvent<HTMLDivElement>) => {
    // Wegen Pointer-Capture ist e.target der Container – Element unter dem Zeiger ermitteln
    const under = document.elementFromPoint(e.clientX, e.clientY);
    const nodeEl = under && under.closest && under.closest('[data-node]');
    if (!nodeEl) return;
    const n = byId(data.nodes, nodeEl.getAttribute('data-node'));
    if (n && n.subprocessId) onDrill(n.subprocessId);
  };

  // Mausrad nicht passiv binden, sonst greift preventDefault nicht
  useEffect(() => {
    const w = wrapRef.current!;
    w.addEventListener('wheel', onWheel, { passive: false });
    return () => w.removeEventListener('wheel', onWheel);
  });

  const add = (typ: string) => {
    const after: any = sel && sel.type === 'node' ? sel.id : null;
    const id = placeNode(pid, { typ, after });
    onSelect({ type: 'node', id });
  };

  // ---------- Kontextmenü: Schritt, Verbindung, leere Fläche ----------
  const linkZu = (rest = '') => adresse(`/p/${encodeURIComponent(sync.projektId)}/prozess/${encodeURIComponent(pid)}${rest}`);
  const schrittEintraege = (n: any): Zeile[] => {
    const zeigen = () => onSelect({ type: 'node', id: n.id });
    return schrittMenue(n, { edit, darfPflegen: darf('verantwortlich'), darfMitwirken: darf('mitwirkend'), gespeichert: aufServer('nodes', n.id) }, {
      oeffnen: zeigen,
      unterprozess: n.subprocessId ? () => onDrill(n.subprocessId) : null,
      bearbeiten: () => { zeigen(); if (!edit) onBearbeiten?.(); void feldFokussieren('[data-testid=node-title]'); },
      status: (v) => setNodeField(n.id, 'status', v),
      aufgabe: () => { zeigen(); void feldFokussieren('[data-testid=aufgaben-schritt] [data-testid=bezug-aufgabe-neu]'); },
      dokument: async () => {
        zeigen();
        const el = await wartenAuf('[data-testid=dokumente-schritt] [data-testid=dokument-hochladen], [data-testid=dokumente-schritt] [data-testid=dokumente-aus]');
        if (el?.dataset.testid === 'dokument-hochladen') { el.scrollIntoView({ block: 'center' }); el.click(); }
        else toast('Die Dokumentablage ist noch nicht eingerichtet.', true);
      },
      linkKopieren: () => kopieren(linkZu('/n/' + encodeURIComponent(n.id))),
      nachfolger: () => onSelect({ type: 'node', id: addNode(n.processId, { after: n.id }) }),
      loeschen: async () => { if (await schrittLoeschen(n)) onSelect(null); },
    }, STATUS);
  };
  const onContextMenu = (e: RMouseEvent<HTMLDivElement>) => {
    const t = e.target as Element;
    // Tastatur (Shift+F10): ausgewählter Schritt bzw. ausgewählte Verbindung
    const nodeId = t.closest('[data-node]')?.getAttribute('data-node') ?? (perTastatur(e) && sel?.type === 'node' ? sel.id : null);
    const edgeId = t.closest('[data-edge]')?.getAttribute('data-edge') ?? (perTastatur(e) && sel?.type === 'edge' ? sel.id : null);
    const n = nodeId ? byId(data.nodes, nodeId) : null;
    const ed = !n && edgeId ? byId(data.edges, edgeId) : null;
    const r = wrapRef.current!.getBoundingClientRect();
    const px = e.clientX - r.left, py = e.clientY - r.top;
    if (nurAnsicht) return menueOeffnen(e, bereinigen([
      { text: 'Details anzeigen', icon: 'info', aktion: () => onSelect(n ? { type: 'node', id: n.id } : { type: 'edge', id: ed.id }), wenn: !!(n || ed) },
      { text: 'Unterprozess öffnen', icon: 'chevron', aktion: () => onDrill(n.subprocessId), wenn: !!n?.subprocessId },
      '-',
      { text: 'Ganzes Diagramm einpassen', icon: 'fit', aktion: () => fit(true) },
      { text: 'Vergrößern', icon: 'zoomIn', aktion: () => zoom(1.2, px, py) },
      { text: 'Verkleinern', icon: 'zoomOut', aktion: () => zoom(1 / 1.2, px, py) },
      { text: legend ? 'Legende ausblenden' : 'Legende einblenden', icon: 'legend', aktion: () => setLegend(!legend) },
    ]));
    if (n) return menueOeffnen(e, schrittEintraege(n));
    if (ed) return menueOeffnen(e, bereinigen([
      { text: 'Details anzeigen', icon: 'info', aktion: () => onSelect({ type: 'edge', id: ed.id }), testid: 'km-oeffnen' },
      { text: 'Zum Start', icon: 'back', aktion: () => onSelect({ type: 'node', id: ed.from }) },
      { text: 'Zum Ziel', icon: 'chevron', aktion: () => onSelect({ type: 'node', id: ed.to }) },
      { text: 'Link kopieren', icon: 'link', aktion: () => kopieren(linkZu('/e/' + encodeURIComponent(ed.id))) },
      '-',
      { text: 'Richtung umkehren', icon: 'swap', aktion: () => reverseEdge(ed.id), wenn: edit },
      { text: 'Löschen', icon: 'trash', gefahr: true, aktion: () => { deleteEdge(ed.id); onSelect(null); }, wenn: edit },
    ]));
    menueOeffnen(e, bereinigen([
      { text: 'Neuer Schritt', icon: 'plus', aktion: () => add('aktivitaet'), wenn: edit },
      { text: 'Neue Verzweigung', icon: 'plus', aktion: () => add('gateway'), wenn: edit },
      { text: 'Neue Entscheidung', icon: 'decision', aktion: () => add('entscheidung'), wenn: edit },
      '-',
      { text: 'Ganzes Diagramm einpassen', icon: 'fit', aktion: () => fit(true) },
      { text: 'Vergrößern', icon: 'zoomIn', aktion: () => zoom(1.2, px, py) },
      { text: 'Verkleinern', icon: 'zoomOut', aktion: () => zoom(1 / 1.2, px, py) },
      { text: legend ? 'Legende ausblenden' : 'Legende einblenden', icon: 'legend', aktion: () => setLegend(!legend) },
      '-',
      { text: 'Link zu diesem Prozess kopieren', icon: 'link', aktion: () => kopieren(linkZu()) },
    ]));
  };

  return (
    <>
      <div className={'diagram' + (edit ? ' edit' : '')} ref={wrapRef} data-testid="diagramm-flaeche"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} onDoubleClick={onDblClick}
        onContextMenu={onContextMenu}>
        <svg xmlns="http://www.w3.org/2000/svg" data-testid="diagram">
          <g ref={gRef}>
            <g dangerouslySetInnerHTML={{ __html: inner }} />
            <g ref={overlayRef} />
          </g>
        </svg>
      </div>
      <div className="dtools">
        <div className="grp">
          <button className="btn icon" title={"Ganzes Diagramm einpassen\nZeigt den ganzen Prozess auf einen Blick."} data-testid="fit" onClick={() => fit(true)}><Icon name="fit" /></button>
          <button className="btn icon" title={"Vergrößern\nAuch mit Strg/⌘ + Mausrad oder zwei Fingern."} onClick={() => zoom(1.2)}><Icon name="zoomIn" /></button>
          <button className="btn icon" title={"Verkleinern\nAuch mit Strg/⌘ + Mausrad oder zwei Fingern."} onClick={() => zoom(1 / 1.2)}><Icon name="zoomOut" /></button>
          <button className={'btn small' + (legend ? ' on' : '')} title={"Legende ein-/ausblenden\nErklärt Verbindungsarten, Statusfarben und Kennzeichen an den Schritten."} data-testid="legend-toggle" onClick={() => setLegend(!legend)}><Icon name="legend" /> Legende</button>
        </div>
        {edit && (
          <div className="grp symbolleiste" data-testid="symbolleiste" role="toolbar" aria-label="Schritt hinzufügen">
            {PALETTE.map((typ) => (
              <button key={typ} className="btn icon symbol" data-testid={'symbol-' + typ}
                title={`${typName(typ)}\n${TYP_INFO[typ]}\nZiehen: an eine freie Stelle, auf einen Schritt (wird Nachfolger) oder auf einen Pfeil (wird eingefügt).\n`
                  + (sel && sel.type === 'node' ? 'Klicken: als Nachfolger des ausgewählten Schritts anlegen.' : 'Klicken: in der ersten Bahn anlegen.')}
                aria-label={typName(typ)}
                onPointerDown={(e) => paletteDown(e, typ)} onPointerMove={paletteMove} onPointerUp={paletteUp} onPointerCancel={paletteCancel}
                onClick={() => paletteClick(typ)}>
                <Symbol typ={typ} />
              </button>
            ))}
          </div>
        )}
      </div>
      {legend && <Legend data={data} />}
      {edit && <div className="edit-hint">Symbol oben ins Diagramm ziehen = neuer Schritt (auf Schritt/Pfeil = gleich verbunden) · Schritt ziehen = verschieben · Punkt rechts ziehen = verbinden · Entf = löschen</div>}
    </>
  );
}
